import { useEffect, useMemo, useRef, useState } from "react";
import {
  ChevronDown20Regular,
  Search20Regular,
} from "@fluentui/react-icons";
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from "@/components/ui/popover";
import { InfoHint } from "@/components/ui/info-hint";
import { cn } from "@/lib/utils";

const normalize = (s) => String(s ?? "").toLowerCase();

/**
 * Combobox — searchable single-value dropdown.
 *
 * Trigger matches the height/border treatment of `<Input>` and `<SelectTrigger>`,
 * so it sits flush in the same form grids. The panel always renders a search
 * field, a radio-dot indicator for the selected row, and a yellow tint on
 * hover/selection (brand `--primary-color`).
 *
 * `options` may be `string[]` or `{ value, label, description? }[]`. An option
 * with a `description` gets an info icon next to its label; the text shows on
 * hover.
 *
 * `clearable` puts an extra row at the top of the list (labelled with the
 * `placeholder`) that resets the field to `null` — without it a single-select
 * can never be emptied again once something was picked. Deliberately a list
 * row, not an × in the trigger: the panel is a radio group, so "nothing
 * selected" belongs in it as an option.
 *
 * @example
 *   <Combobox
 *     value={location}
 *     options={locations}
 *     onChange={setLocation}
 *     placeholder="Standort wählen…"
 *   />
 */
export function Combobox({
  value,
  options = [],
  onChange,
  placeholder = "Auswählen…",
  searchPlaceholder = "Suchen…",
  emptyMessage = "Keine Ergebnisse",
  disabled = false,
  clearable = false,
  className,
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const triggerRef = useRef(null);
  const searchRef = useRef(null);
  const [triggerWidth, setTriggerWidth] = useState();

  // Wheel-Scroll-Workaround: Wenn der Combobox in einem Radix-Dialog steckt,
  // blockiert dessen `react-remove-scroll` alle Wheel-Events ausserhalb des
  // Dialog-Trees. Da wir nach `document.body` portalieren (damit nichts
  // clipped wird), müssen wir das Scrollen in der Optionsliste manuell
  // handhaben — der Handler ist direkt am JSX-Element via `onWheel`.
  const handleWheel = (e) => {
    e.currentTarget.scrollTop += e.deltaY;
    e.stopPropagation();
  };

  const items = useMemo(
    () =>
      options.map((opt) =>
        typeof opt === "string"
          ? { value: opt, label: opt }
          : {
              value: opt.value,
              label: opt.label ?? String(opt.value),
              description: opt.description || null,
            },
      ),
    [options],
  );

  const filtered = useMemo(() => {
    if (!search.trim()) return items;
    const q = normalize(search);
    return items.filter((i) => normalize(i.label).includes(q));
  }, [items, search]);

  const current = items.find((i) => i.value === value);

  useEffect(() => {
    if (!open) {
      setSearch("");
      return;
    }
    if (triggerRef.current) {
      setTriggerWidth(triggerRef.current.offsetWidth);
    }
    const t = setTimeout(() => searchRef.current?.focus(), 30);
    return () => clearTimeout(t);
  }, [open]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          ref={triggerRef}
          type="button"
          disabled={disabled}
          className={cn(
            "flex w-full items-center justify-between gap-[0.6rem]",
            "h-[3.6rem] px-[1.2rem] text-[1.3rem]",
            "bg-white border-[0.15rem] border-gray rounded-[0.8rem]",
            "outline-none transition-[border-color,box-shadow] duration-150 cursor-pointer",
            "font-[inherit] text-left",
            open
              ? "border-primary shadow-[0_0_0_0.3rem_rgba(200,212,0,0.15)]"
              : "hover:border-[#d4d4d8]",
            "disabled:bg-bg disabled:cursor-not-allowed disabled:opacity-60",
            className,
          )}
          aria-expanded={open}
          aria-haspopup="listbox"
        >
          <span
            className={cn(
              "truncate",
              current ? "text-foreground" : "text-dim",
            )}
          >
            {current ? current.label : placeholder}
          </span>
          <ChevronDown20Regular
            className={cn(
              "w-[1.4rem] h-[1.4rem] text-dim flex-shrink-0 transition-transform",
              open && "rotate-180",
            )}
          />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={6}
        className="p-0 overflow-hidden"
        style={{ width: triggerWidth }}
      >
        {items.length > 4 && (
          <div className="px-[0.8rem] pt-[0.8rem] pb-[0.6rem] border-b border-gray">
            <div className="relative">
              <Search20Regular className="absolute left-[0.8rem] top-1/2 -translate-y-1/2 w-[1.4rem] h-[1.4rem] text-[#a1a1aa] pointer-events-none" />
              <input
                ref={searchRef}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={searchPlaceholder}
                className={cn(
                  "w-full pl-[2.8rem] pr-[1rem] py-[0.6rem]",
                  "border border-gray rounded-[0.6rem] text-[1.2rem]",
                  "outline-none transition-colors",
                  "placeholder:text-dim font-[inherit]",
                  "focus:border-primary",
                )}
              />
            </div>
          </div>
        )}
        <div
          onWheel={handleWheel}
          className="max-h-[24rem] overflow-y-auto p-[0.4rem]"
        >
          {/* Leer-Auswahl: nur ohne aktive Suche, sie ist kein Suchtreffer. */}
          {clearable && !search.trim() && (
            <button
              type="button"
              onClick={() => {
                onChange?.(null);
                setOpen(false);
              }}
              className={cn(
                "flex w-full items-center gap-[0.8rem]",
                "px-[1rem] py-[0.8rem] rounded-[0.6rem]",
                "text-[1.3rem] text-dim text-left font-[inherit]",
                "transition-colors cursor-pointer border-none",
                current ? "bg-transparent hover:bg-[#f4f4f5]" : "bg-[#fefce8]",
              )}
            >
              <span
                className={cn(
                  "flex w-[1.6rem] h-[1.6rem] flex-shrink-0",
                  "items-center justify-center rounded-full border-[0.15rem]",
                  current ? "border-[#d4d4d8]" : "border-primary",
                )}
              >
                {!current && (
                  <span className="w-[0.8rem] h-[0.8rem] rounded-full bg-primary" />
                )}
              </span>
              <span className="truncate">{placeholder}</span>
            </button>
          )}
          {filtered.length === 0 ? (
            <div className="px-[1.2rem] py-[1rem] text-[1.2rem] text-dim">
              {emptyMessage}
            </div>
          ) : (
            filtered.map((item) => {
              const isSelected = item.value === value;
              return (
                <button
                  key={item.value}
                  type="button"
                  onClick={() => {
                    onChange?.(item.value);
                    setOpen(false);
                  }}
                  className={cn(
                    "flex w-full items-center gap-[0.8rem]",
                    "px-[1rem] py-[0.8rem] rounded-[0.6rem]",
                    "text-[1.3rem] text-foreground text-left font-[inherit]",
                    "transition-colors cursor-pointer border-none",
                    isSelected ? "bg-[#fefce8]" : "bg-transparent",
                    !isSelected && "hover:bg-[#f4f4f5]",
                  )}
                >
                  <span
                    className={cn(
                      "flex w-[1.6rem] h-[1.6rem] flex-shrink-0",
                      "items-center justify-center rounded-full border-[0.15rem]",
                      isSelected ? "border-primary" : "border-[#d4d4d8]",
                    )}
                  >
                    {isSelected && (
                      <span className="w-[0.8rem] h-[0.8rem] rounded-full bg-primary" />
                    )}
                  </span>
                  <span className="truncate">{item.label}</span>
                  <InfoHint text={item.description} />
                </button>
              );
            })
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
